import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { get_offsets } from "./parts/Stage";
import { Loader, OffsetImage } from "./StyledComponents";

// Constants
export const INITIAL_URL = 'https://byob.jensen-beds.com';

// GraphQL Queries
const QUERIES = {
  HEADBOARD: gql`
    query headboardImage($hid: Int!, $headboard: Int!) {
      headboardByobImage(hid: $hid, headboard: $headboard) {
        url
        type
      }
    }
  `,
  TOPPER: gql`
    query topperImage($id: Int!, $tid: Int!, $bid: Int!) {
      topperByobImage(id: $id, tid: $tid, bid: $bid) {
        url
        type
      }
    }
  `,
  // ... other queries
};

// Helper function for safe URL joining
const joinUrl = (base, path) => {
  try {
    // If path already starts with the base URL, don't add it again
    if (path?.startsWith(base)) {
      return path;
    }
    // If path is already a full URL, return it
    if (path?.startsWith('http')) {
      return path;
    }
    // Remove any leading slashes from path
    const cleanPath = path?.replace(/^\/+/, '');
    return new URL(cleanPath, base).toString();
  } catch (e) {
    console.error('Invalid URL:', { base, path });
    return null;
  }
};

// Custom hook for handling common query patterns
const useByobQuery = (query, variables) => {
  const { loading, error, data } = useQuery(query, { variables });

  if (error) {
    console.error('Query error:', error);
    return { loading: false, error, data: null };
  }

  return { loading, error, data };
};

// Component for rendering images with proper error handling
export const SafeImage = ({ src, alt, className, offset, voffset }) => {
  const safeUrl = joinUrl(INITIAL_URL, src);
  if (!safeUrl) return null;

  return (
    <OffsetImage
      src={safeUrl}
      alt={alt}
      className={className}
      offset={offset}
      voffset={voffset}
    />
  );
};

// Main components with improved error handling and type safety
const RenderHeadboard = ({ selected, offset = 0 }) => {
  const { loading, data } = useByobQuery(QUERIES.HEADBOARD, {
    headboard: selected.headboards?.id,
    hid: selected.textiles?.hid,
  });

  if (loading) return <Loader />;
  if (!data?.headboardByobImage) return null;

  return (
    <SafeImage
      src={data.headboardByobImage.url}
      alt={`headboard-${selected.headboards}`}
      className={`headboard headboard-${data.headboardByobImage.type} ${data.headboardByobImage.type}`}
      offset={offset}
    />
  );
};

const RenderTopper = ({ selected, offset }) => {
  const { loading, error, data } = useQuery(
    gql`
      query topperImage($id: Int!, $tid: Int!, $bid: Int!) {
        topperByobImage(id: $id, tid: $tid, bid: $bid) {
          url
          type
        }
      }
    `,
    {
      variables: {
        id: selected.topper.id,
        tid: selected.textiles.id,
        bid: selected.bed.id,
      },
    }
  );
  if (error) return null;
  if (loading) return <Loader />;

  if (data.topperByobImage) {
    return (
      <SafeImage
        src={data.topperByobImage.url}
        alt={selected.topper.title}
        offset={offset}
      />
    );
  }
  return null;
};

const RenderTextileOttoman = ({ selected, offset }) => {
  const { loading, error, data } = useQuery(
    gql`
      query simpleOttoman($ottoman: Int!, $id: Int!, $legs: Int!) {
        ottomanByobImage(ottoman: $ottoman, id: $id, legs: $legs) {
          url
          type
        }
      }
    `,
    {
      variables: {
        ottoman: selected.ottoman.id,
        id: selected.textiles.id,
        legs: selected.legs.id,
      },
    }
  );
  console.log(data, error, loading);
  if (error) return null;
  if (loading) return <Loader />;

  if (data.ottomanByobImage.length > 0) {
    return data.ottomanByobImage.map((otto) => (
      <SafeImage
        src={otto.url}
        key={`${otto.id}-${otto.type}`}
        alt={`ottoman-${otto.id}`}
        className={`ottoman ottoman-${otto.type} ${otto.type}`}
        offset={otto.type === "image" ? offset : 0}
      />
    ));
  }
  return null;
};

const RenderSimpleOttoman = ({ selected }) => {
  const { loading, error, data } = useQuery(
    gql`
      query simpleOttoman($ottoman: Int!) {
        ottomanByobImage(ottoman: $ottoman) {
          url
          type
        }
      }
    `,
    {
      variables: {
        ottoman: selected.ottoman.id,
      },
    }
  );

  if (error) return null;
  if (loading) return <Loader />;
  if (data.ottomanByobImage.length > 0) {
    return data.ottomanByobImage.map((otto) => (
      <OffsetImage
        src={otto.url}
        key={`${otto.id}-${otto.type}`}
        alt={`ottoman-${otto.id}`}
        className={`ottoman ottoman-${otto.type} ${otto.type}`}
      />
    ));
  }
  return null;
};

const RenderOttoman = ({ selected, offset }) => {
  if (selected.ottoman.single) {
    return <RenderSimpleOttoman selected={selected} />;
  } else if (selected.textiles && selected.textiles.id) {
    return <RenderTextileOttoman selected={selected} offset={offset} />;
  }
  return null;
};

const RenderTextileAddOn = ({ addon, selected, type, offset = 0 }) => {
  const { loading, error, data } = useQuery(
    gql`
      query textileAddOn($id: Int!, $tid: Int!) {
        addonByobImage(id: $id, tid: $tid) {
          type
          url
        }
      }
    `,
    {
      variables: {
        id: addon.id,
        tid: selected.textiles.id,
      },
    }
  );
  if (error) return null;
  if (loading) return <Loader />;
  if (data.addonByobImage.length > 0) {
    return data.addonByobImage.map((img) => (
      <OffsetImage
        src={img.url}
        key={`${img.id}-${img.type}`}
        alt={`addon-${img.id}`}
        className={`addon addon-${img.type} ${img.type}`}
        offset={img.type === "sub_mat" ? offset : 0}
      />
    ));
  }
  return null;
};

const RenderSimpleAddOn = ({ addon, type, compat, offset, pop, voffset }) => {
  const { loading, error, data } = useQuery(
    gql`
      query simpleAddOn($id: Int!) {
        addonByobImage(id: $id) {
          type
          url
        }
      }
    `,
    {
      variables: {
        id: addon?.id,
      },
    }
  );
  if (error) return null;
  if (loading) return <Loader />;
  const this_addon = data.addonByobImage.find((img) => img.type === type);

  if (!compat) return null;
  if (!this_addon) return null;
  return (
    <SafeImage
      voffset={voffset}
      src={this_addon.url}
      alt={`AddOn-${addon.id}`}
      className={`AddOn AddOn-${this_addon.type} ${this_addon.type} ${
        pop && "AddOn-pop"
      }`}
      offset={offset}
    />
  );
};

const RenderAddOn = ({ selected, type, offset, index = 0 }) => {
  if (type === "sub_mat") {
    const pocket = selected.addons?.filter(
      (p) => p.title.toLowerCase().includes("pocket")
    )[0];
    
    if (pocket) {
      return (
        <RenderTextileAddOn
          addon={pocket}
          type={type}
          selected={selected}
          offset={offset}
        />
      );
    }
  } else {
    const lamp = selected.addons?.filter(
      (p) => p.title.toLowerCase().includes("lamp") || p.title.toLowerCase().includes("tray")
    );

    let voffset = 0;
    const table = selected?.bedsidetable?.title;
    if (
      table?.toLowerCase().includes("add-on") &&
      !table?.toLowerCase().includes("add-on floor")
    ) {
      voffset -= 2;
    }
    if (type === "image_2") {
      voffset *= -1;
    }

    if (lamp?.length) {
      const isTray = lamp[0].title.toLowerCase().includes("tray");
      const isTableLamp = lamp[index]?.title?.toLowerCase().includes("table lamp");
      
      const adjustedOffset = isTray 
        ? get_offsets(selected, "tray")
        : isTableLamp 
          ? get_offsets(selected, "tableLamp", type === "image_2")
          : offset;

      return (
        <RenderSimpleAddOn
          pop={type === "image" && isTableLamp}
          voffset={voffset}
          addon={lamp[index]}
          type={type}
          offset={adjustedOffset}
          compat={
            isTray ||
            isTableLamp ||
            (selected.legs?.compat ?? true) &&
            (selected.headboards?.compat ?? true)
          }
        />
      );
    }
  }
  return null;
};

const RenderLegs = ({ selected, type = "image" }) => {
  const { loading, error, data } = useQuery(
    gql`
      query legsImage($id: Int!, $bed: Int!) {
        legsByobImage(id: $id, bed: $bed) {
          url
          type
        }
      }
    `,
    {
      variables: {
        id: selected.legs ? selected.legs.id : 0,
        bed: selected.bed.id,
      },
    }
  );

  if (error) return null;
  if (loading) return <Loader />;
  if (data.legsByobImage.length >= 2) {
    const image = data.legsByobImage.find((img) => {
      return img.type === type;
    });
    return (
      <>
        {image && (
          <SafeImage
            src={image.url}
            alt={`legs-${selected.legs.id}`}
            className={`legs legs-${image.type} ${image.type}`}
          />
        )}
      </>
    );
  }
  return null;
};

const RenderTable = ({ selected, offset = 0, placement = "front" }) => {
  const isCompat = () => {
    if (selected.bedsidetable.on_floor) return true;
    if (selected.headboards) return selected.headboards.compat;
    return true;
  };

  if (!isCompat()) return null;

  if (placement === "front") {
    return (
      <SafeImage
        src={selected.bedsidetable.byob_front.url}
        alt={`bedsidetable-front-${selected.bedsidetable.id}`}
        offset={offset}
      />
    );
  }

  if (placement === "back") {
    return (
      <OffsetImage
        src={INITIAL_URL + selected.bedsidetable.byob_back.url}
        alt={`bedsidetable-back-${selected.bedsidetable.id}`}
        offset={offset}
      />
    );
  }
};

export {
  RenderHeadboard,
  RenderLegs,
  RenderOttoman,
  RenderAddOn,
  RenderTopper,
  RenderTable,
};
